/**
 * navBillboardService.js
 *
 * Renders editor-authored navigation billboards as REAL 3D meshes inside
 * the Matterport scene. Unlike POI / Nav-pill markers (which are DOM
 * overlays projected to screen space and always face the camera), a
 * billboard is fully world-anchored — board + base stand sit on the
 * ground at a fixed yaw, exactly like a road-side signage board.
 *
 * Source of truth for the data:
 *   navHintsService.getAll().filter(h => h.type === 'billboard')
 *
 * Hot-reload model: subscribes to navHintsService and rebuilds individual
 * meshes on add / update / remove. Re-renders the canvas texture on
 * language change (setLanguage).
 *
 * Usage:
 *   navBillboardService.initialize(sdk, language)  — call once after SDK connects
 *   navBillboardService.setLanguage(lang)          — language change hook
 *   navBillboardService.destroy()
 */

import navHintsService from './navHintsService';

const COMPONENT_NAME = 'mp.navBillboard';

// Default real-world reference dimensions (metres) at scale=1. The whole
// rig (board + stand) scales linearly with `scale`.
const REF_BOARD_WIDTH  = 1.6;
const REF_BOARD_HEIGHT = 1.0;
const REF_STAND_HEIGHT = 1.4;
const REF_POST_RADIUS  = 0.04;

const TEXTURE_W = 1024;
const TEXTURE_H = 640;

let _THREE = null; // eslint-disable-line no-unused-vars
const _meshRegistry = new Map();   // id → THREE.Group
const _canvasRegistry = new Map(); // id → HTMLCanvasElement

// ─── Canvas painter ────────────────────────────────────────────────────────

/**
 * Paint the billboard face onto a transparent canvas. The plane mesh
 * uses transparency, so anything drawn outside the chosen `boardShape`
 * silhouette stays invisible — this is how we fake non-rectangular
 * board outlines (peaked top, oval, …) without custom geometry.
 */
function paintBoardCanvas(hint, language) {
  const canvas = _canvasRegistry.get(hint.id) || document.createElement('canvas');
  canvas.width = TEXTURE_W;
  canvas.height = TEXTURE_H;
  _canvasRegistry.set(hint.id, canvas);

  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, TEXTURE_W, TEXTURE_H);

  const fill   = hint.boardColor       || '#FFFFFF';
  const text   = hint.boardTextColor   || '#111111';
  const border = hint.boardBorderColor || '#111111';
  const shape  = hint.boardShape       || 'rect';

  // Build the board silhouette as a path, then fill + stroke + clip the
  // text to it. Every path is inset by `pad` so the stroke isn't clipped
  // by the canvas edge.
  const pad = 12;
  const x = pad;
  const y = pad;
  const w = TEXTURE_W - pad * 2;
  const h = TEXTURE_H - pad * 2;

  ctx.save();
  ctx.lineWidth = 14;
  ctx.lineJoin = 'round';

  ctx.beginPath();
  if (shape === 'rounded') {
    const r = Math.min(w, h) * 0.10;
    ctx.roundRect(x, y, w, h, r);
  } else if (shape === 'peaked') {
    // House-style: triangular cap on top of a rectangle.
    const peakH = h * 0.18;
    ctx.moveTo(x, y + peakH);
    ctx.lineTo(x + w / 2, y);
    ctx.lineTo(x + w, y + peakH);
    ctx.lineTo(x + w, y + h);
    ctx.lineTo(x, y + h);
    ctx.closePath();
  } else if (shape === 'oval') {
    ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
  } else {
    ctx.rect(x, y, w, h);
  }

  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = border;
  ctx.stroke();

  // Clip text to the board silhouette.
  ctx.clip();

  // ── Label ────────────────────────────────────────────────────────────
  const label = (hint.labels && (hint.labels[language] || hint.labels.en
                  || hint.labels.mr || hint.labels.hi)) || '';

  if (label) {
    ctx.fillStyle = text;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Auto-fit: shrink font until the longest line fits within ~80% width.
    const lines = label.split('\n').map(s => s.trim()).filter(Boolean);
    const maxLineLen = Math.max(1, ...lines.map(l => l.length));
    const baseSize = Math.min(180, Math.max(48, (w * 0.85) / (maxLineLen * 0.5)));
    const fontSize = Math.max(36, baseSize / Math.max(1, lines.length * 0.6));
    ctx.font = `700 ${fontSize}px "Noto Sans Devanagari", "Noto Sans", Arial, sans-serif`;

    const lineHeight = fontSize * 1.25;
    const totalH = lines.length * lineHeight;
    const startY = TEXTURE_H / 2 - (totalH - lineHeight) / 2;
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], TEXTURE_W / 2, startY + i * lineHeight);
    }
  }

  ctx.restore();
  return canvas;
}

// ─── SDK Scene component ───────────────────────────────────────────────────

function NavBillboardComponent() {
  this.inputs = {
    hintId: '',
    worldX: 0,
    worldY: 0,
    worldZ: 0,
    rotationYDeg: 0,
    scale: 1.0,
    baseStand: 'singlePost',
    standColor: '#7d7d7d',
  };
  this._group = null;
  this._board = null;
  this._stand = null;
  this._mat = null;
  this._tex = null;
}

NavBillboardComponent.prototype.onInit = function () {
  try {
    const THREE = this.context.three;
    _THREE = THREE;

    const {
      hintId, worldX, worldY, worldZ,
      rotationYDeg, scale, baseStand, standColor,
    } = this.inputs;

    const canvas = _canvasRegistry.get(hintId);
    if (!canvas) {
      console.warn(`🪧 NavBillboard: no canvas registered for "${hintId}"`);
      return;
    }

    const group = new THREE.Group();

    // ── Board (vertical plane with canvas texture) ──────────────────────
    this._tex = new THREE.CanvasTexture(canvas);
    this._tex.needsUpdate = true;
    // Higher anisotropy = readable text at oblique angles.
    if (this.context.renderer?.capabilities?.getMaxAnisotropy) {
      this._tex.anisotropy = this.context.renderer.capabilities.getMaxAnisotropy();
    }

    this._mat = new THREE.MeshBasicMaterial({
      map: this._tex,
      side: THREE.DoubleSide,
      transparent: true,
      alphaTest: 0.05,
      depthWrite: false,
    });

    const boardW = REF_BOARD_WIDTH * scale;
    const boardH = REF_BOARD_HEIGHT * scale;
    const standH = REF_STAND_HEIGHT * scale;

    const boardGeo = new THREE.PlaneGeometry(boardW, boardH);
    this._board = new THREE.Mesh(boardGeo, this._mat);
    this._board.position.set(0, standH + boardH / 2, 0);
    group.add(this._board);

    // ── Base stand ──────────────────────────────────────────────────────
    if (baseStand !== 'none') {
      const standMat = new THREE.MeshStandardMaterial({
        color: new THREE.Color(standColor || '#7d7d7d'),
        roughness: 0.55,
        metalness: 0.35,
      });
      const standGroup = new THREE.Group();

      if (baseStand === 'plinth') {
        const w = boardW * 0.95;
        const d = REF_POST_RADIUS * 8 * scale;
        const geo = new THREE.BoxGeometry(w, standH, d);
        const m = new THREE.Mesh(geo, standMat);
        m.position.set(0, standH / 2, 0);
        standGroup.add(m);
      } else if (baseStand === 'doublePost') {
        const r = REF_POST_RADIUS * scale;
        const geo = new THREE.CylinderGeometry(r, r, standH, 16);
        const offsetX = boardW * 0.40;
        const left = new THREE.Mesh(geo, standMat);
        const right = new THREE.Mesh(geo, standMat);
        left.position.set(-offsetX, standH / 2, 0);
        right.position.set( offsetX, standH / 2, 0);
        standGroup.add(left);
        standGroup.add(right);
      } else { // 'singlePost'
        const r = REF_POST_RADIUS * 1.5 * scale;
        const geo = new THREE.CylinderGeometry(r, r, standH, 20);
        const m = new THREE.Mesh(geo, standMat);
        m.position.set(0, standH / 2, 0);
        standGroup.add(m);
      }

      this._stand = standGroup;
      group.add(standGroup);
    }

    group.position.set(worldX, worldY, worldZ);
    group.rotation.y = (rotationYDeg * Math.PI) / 180;

    this.context.scene.add(group);
    this._group = group;
    _meshRegistry.set(hintId, group);
  } catch (err) {
    console.error('🪧 NavBillboardComponent.onInit failed:', err?.message, err);
  }
};

NavBillboardComponent.prototype.onDestroy = function () {
  const id = this.inputs.hintId;
  try {
    if (this._group && this.context?.scene) {
      this.context.scene.remove(this._group);
    }
    this._group?.traverse?.((node) => {
      if (node.geometry) node.geometry.dispose?.();
      if (node.material) {
        if (Array.isArray(node.material)) node.material.forEach(m => m.dispose?.());
        else node.material.dispose?.();
      }
    });
    this._tex?.dispose?.();
  } catch (_) { /* noop */ }
  _meshRegistry.delete(id);
};

// ─── Service ───────────────────────────────────────────────────────────────

class NavBillboardService {
  constructor() {
    this._sdk = null;
    this._registered = false;
    this._language = 'mr';
    this._instances = new Map();   // id → { sceneObject }
    this._unsubscribe = null;

    if (typeof window !== 'undefined') {
      window._navBillboardService = this; // console debugging
    }
  }

  async initialize(sdk, language = 'mr') {
    this._sdk = sdk;
    this._language = language;

    if (!this._registered) {
      try {
        await sdk.Scene.register(COMPONENT_NAME, function () {
          return new NavBillboardComponent();
        });
        this._registered = true;
        console.log('🪧 NavBillboardService: component registered');
      } catch (err) {
        console.warn('🪧 NavBillboardService: failed to register component:', err?.message);
        return;
      }
    }

    // Initial sync — render whatever billboards are already in the store
    // (e.g. loaded from /config/navHintsConfig.json on boot).
    await this._syncAll();

    // Live updates from the editor / config reloads.
    this._unsubscribe = navHintsService.subscribe(() => {
      this._syncAll().catch(err =>
        console.warn('🪧 NavBillboardService: sync failed:', err?.message)
      );
    });
  }

  async _syncAll() {
    if (!this._sdk || !this._registered) return;
    const all = navHintsService.getAll().filter(h => h.type === 'billboard');
    const wantedIds = new Set(all.map(h => h.id));

    // Drop billboards no longer present.
    for (const id of Array.from(this._instances.keys())) {
      if (!wantedIds.has(id)) this._remove(id);
    }

    // Create / update each billboard.
    for (const h of all) {
      const existing = this._instances.get(h.id);
      if (!existing) {
        await this._create(h).catch(err =>
          console.warn(`🪧 NavBillboard "${h.id}" create failed:`, err?.message)
        );
      } else {
        this._update(h);
      }
    }
  }

  async _create(hint) {
    if (!this._sdk) return;
    paintBoardCanvas(hint, this._language);

    const [sceneObject] = await this._sdk.Scene.createObjects(1);
    const node = sceneObject.addNode();
    node.addComponent(COMPONENT_NAME, {
      hintId: hint.id,
      worldX: hint.position?.x ?? 0,
      worldY: hint.position?.y ?? 0,
      worldZ: hint.position?.z ?? 0,
      rotationYDeg: hint.rotationY ?? 0,
      scale: hint.scale ?? 1.0,
      baseStand: hint.baseStand || 'singlePost',
      standColor: hint.standColor || '#7d7d7d',
    });
    sceneObject.start();
    this._instances.set(hint.id, { sceneObject, snapshot: this._snapshot(hint) });
  }

  /**
   * Capture the subset of fields that influence either geometry or
   * texture so `_update` can detect if a full rebuild is required.
   */
  _snapshot(h) {
    return JSON.stringify({
      pos: h.position,
      rot: h.rotationY ?? 0,
      scale: h.scale ?? 1.0,
      stand: h.baseStand || 'singlePost',
      standColor: h.standColor,
      shape: h.boardShape,
      board: h.boardColor,
      txt: h.boardTextColor,
      border: h.boardBorderColor,
      labels: h.labels,
    });
  }

  _update(hint) {
    const inst = this._instances.get(hint.id);
    if (!inst) return;

    const snap = this._snapshot(hint);
    if (snap === inst.snapshot) return; // no relevant change

    // Geometry / stand / scale / rotation can change cheaply on the live
    // group. Texture-affecting changes require a canvas repaint.
    const group = _meshRegistry.get(hint.id);

    // Repaint texture (board outline / colours / labels).
    paintBoardCanvas(hint, this._language);
    if (group) {
      group.traverse((node) => {
        if (node.material?.map?.needsUpdate !== undefined) {
          node.material.map.needsUpdate = true;
        }
      });
    }

    // Cheap transform updates.
    if (group) {
      const p = hint.position || {};
      group.position.set(p.x ?? 0, p.y ?? 0, p.z ?? 0);
      group.rotation.y = ((hint.rotationY ?? 0) * Math.PI) / 180;
    }

    // Heavy structural changes — scale, stand kind, board shape — need a
    // full rebuild via stop+create. Detect by comparing the snapshot
    // structurally rather than per-field for simplicity.
    let prev = {};
    try { prev = JSON.parse(inst.snapshot); } catch (_) { /* noop */ }
    const structuralChange =
      prev.scale !== (hint.scale ?? 1.0) ||
      prev.stand !== (hint.baseStand || 'singlePost') ||
      prev.shape !== hint.boardShape ||
      prev.standColor !== hint.standColor;

    if (structuralChange) {
      this._remove(hint.id);
      // fire and forget — sync round will pick this up too
      this._create(hint).catch(() => {});
      return;
    }

    inst.snapshot = snap;
  }

  _remove(id) {
    const inst = this._instances.get(id);
    if (inst) {
      try { inst.sceneObject.stop(); } catch (_) { /* noop */ }
      this._instances.delete(id);
    }
    _meshRegistry.delete(id);
    _canvasRegistry.delete(id);
  }

  setLanguage(language) {
    if (this._language === language) return;
    this._language = language;
    // Repaint every billboard's canvas with the new language.
    const all = navHintsService.getAll().filter(h => h.type === 'billboard');
    for (const h of all) {
      paintBoardCanvas(h, this._language);
      const group = _meshRegistry.get(h.id);
      group?.traverse?.((node) => {
        if (node.material?.map) node.material.map.needsUpdate = true;
      });
    }
  }

  destroy() {
    if (this._unsubscribe) {
      try { this._unsubscribe(); } catch (_) { /* noop */ }
      this._unsubscribe = null;
    }
    for (const id of Array.from(this._instances.keys())) {
      this._remove(id);
    }
  }
}

const navBillboardService = new NavBillboardService();
export default navBillboardService;
